import { useEffect, useState } from "react";
import { X } from "lucide-react";
import GradeFormatFields from "./GradeFormatFields";
import { extractErrorMessage, getFetchErrorMessage } from "../utils/errorUtils";

const GRADE_KINDS = [
  { value: "final", label: "Ocena końcowa" },
  { value: "exam", label: "Egzamin" },
  { value: "assignment", label: "Zadanie" },
  { value: "activity", label: "Inna aktywność" },
];

const EXAM_TYPES = [
  { value: "FINAL", label: "Egzamin końcowy" },
  { value: "MIDTERM", label: "Egzamin śródsemestralny" },
  { value: "RESIT", label: "Egzamin poprawkowy" },
];

const EXAM_STATUSES = [
  { value: "PASSED", label: "Zaliczony" },
  { value: "FAILED", label: "Niezaliczony" },
  { value: "ABSENT", label: "Nieobecny" },
  { value: "EXEMPT", label: "Zwolniony" },
];

const ACTIVITY_TYPES = [
  { value: "PRESENTATION", label: "Prezentacja" },
  { value: "QUIZ", label: "Quiz" },
  { value: "ACTIVITY", label: "Aktywność" },
  { value: "TEST", label: "Test" },
  { value: "OTHER", label: "Inne" },
];

export default function AdminAddGradeModal({
  isOpen,
  onClose,
  onSuccess,
  students,
  groupId,
  moduleId,
  preselectedStudentId = "",
  preselectedKind = "final",
}) {
  const [kind, setKind] = useState(preselectedKind);
  const [studentId, setStudentId] = useState(preselectedStudentId);
  const [gradeFormat, setGradeFormat] = useState("scale_5");
  const [grade, setGrade] = useState("");
  const [gradeDate, setGradeDate] = useState(new Date().toISOString().split("T")[0]);
  const [examType, setExamType] = useState("FINAL");
  const [examStatus, setExamStatus] = useState("FAILED");
  const [attempt, setAttempt] = useState(1);
  const [activityType, setActivityType] = useState("QUIZ");
  const [weight, setWeight] = useState(1);
  const [description, setDescription] = useState("");
  const [assignments, setAssignments] = useState([]);
  const [groupAssignmentId, setGroupAssignmentId] = useState("");
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setKind(preselectedKind);
    setStudentId(preselectedStudentId || "");
    setGrade("");
    setGradeFormat("scale_5");
    setError("");
  }, [isOpen, preselectedKind, preselectedStudentId]);

  useEffect(() => {
    if (!isOpen || kind !== "assignment" || !moduleId || !groupId) {
      setAssignments([]);
      return;
    }

    async function fetchAssignments() {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/grades/admin/assignments?module_id=${moduleId}&group_code=${encodeURIComponent(groupId)}`
        );
        if (!res.ok) {
          throw new Error("Nie udało się pobrać zadań");
        }
        const data = await res.json();
        setAssignments(data);
        if (data.length > 0) {
          setGroupAssignmentId(String(data[0].group_assignments_id));
        }
      } catch (err) {
        setError(extractErrorMessage(err));
      }
    }

    fetchAssignments();
  }, [isOpen, kind, moduleId, groupId]);

  if (!isOpen) {
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!studentId || !moduleId) {
      setError("Wybierz studenta i przedmiot");
      return;
    }

    setSubmitting(true);
    try {
      let url;
      let body;

      if (kind === "final") {
        url = `${process.env.REACT_APP_API_URL}/grades/admin/grade`;
        body = {
          student_id: parseInt(studentId, 10),
          module_id: parseInt(moduleId, 10),
          grade,
          grade_format: gradeFormat,
          exam_date: gradeDate,
        };
      } else if (kind === "exam") {
        url = `${process.env.REACT_APP_API_URL}/grades/admin/addExam`;
        body = {
          student_id: parseInt(studentId, 10),
          module_id: parseInt(moduleId, 10),
          exam_date: gradeDate,
          grade: grade || null,
          grade_format: gradeFormat,
          exam_type: examType,
          attempt,
          exam_status: examStatus,
        };
      } else if (kind === "assignment") {
        if (!groupAssignmentId) {
          throw new Error("Wybierz zadanie");
        }
        url = `${process.env.REACT_APP_API_URL}/grades/admin/addAssignment`;
        body = {
          student_id: parseInt(studentId, 10),
          group_assignment_id: parseInt(groupAssignmentId, 10),
          grade,
          grade_format: gradeFormat,
          feedback: feedback || null,
        };
      } else {
        url = `${process.env.REACT_APP_API_URL}/grades/admin/addActivity`;
        body = {
          student_id: parseInt(studentId, 10),
          module_id: parseInt(moduleId, 10),
          activity_type: activityType,
          grade,
          grade_format: gradeFormat,
          weight,
          description: description || null,
        };
      }

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const msg = await getFetchErrorMessage(res, "Nie udało się dodać oceny.");
        throw new Error(msg);
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  function renderGradeInput() {
    return (
      <GradeFormatFields
        className="ag-input"
        format={gradeFormat}
        value={grade}
        onFormatChange={(next) => {
          setGradeFormat(next);
          setGrade("");
        }}
        onValueChange={setGrade}
      />
    );
  }

  return (
    <div className="admin-materials-modal-overlay" onMouseDown={onClose}>
      <div className="admin-materials-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="admin-materials-modal-head">
          <div>
            <h3 className="admin-materials-modal-title">Dodaj ocenę</h3>
            <p className="admin-materials-modal-subtitle">Administrator</p>
          </div>
          <button type="button" className="admin-materials-modal-close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form className="admin-materials-modal-body" onSubmit={handleSubmit}>
          <label className="admin-materials-label">
            <span>Typ oceny</span>
            <select className="ag-input" value={kind} onChange={(e) => setKind(e.target.value)}>
              {GRADE_KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </label>

          <label className="admin-materials-label">
            <span>Student</span>
            <select
              className="ag-input"
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
              required
            >
              <option value="">— wybierz —</option>
              {students.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>

          {kind !== "assignment" && (
            <label className="admin-materials-label">
              <span>Ocena / wynik</span>
              {renderGradeInput()}
            </label>
          )}

          {(kind === "final" || kind === "exam") && (
            <label className="admin-materials-label">
              <span>Data</span>
              <input
                type="date"
                className="ag-input"
                value={gradeDate}
                onChange={(e) => setGradeDate(e.target.value)}
                required
              />
            </label>
          )}

          {kind === "exam" && (
            <>
              <label className="admin-materials-label">
                <span>Typ egzaminu</span>
                <select className="ag-input" value={examType} onChange={(e) => setExamType(e.target.value)}>
                  {EXAM_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="admin-materials-label">
                <span>Status</span>
                <select className="ag-input" value={examStatus} onChange={(e) => setExamStatus(e.target.value)}>
                  {EXAM_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="admin-materials-label">
                <span>Próba</span>
                <input
                  type="number"
                  min="1"
                  className="ag-input"
                  value={attempt}
                  onChange={(e) => setAttempt(parseInt(e.target.value, 10) || 1)}
                />
              </label>
            </>
          )}

          {kind === "assignment" && (
            <>
              <label className="admin-materials-label">
                <span>Zadanie</span>
                <select
                  className="ag-input"
                  value={groupAssignmentId}
                  onChange={(e) => setGroupAssignmentId(e.target.value)}
                  required
                >
                  <option value="">— wybierz —</option>
                  {assignments.map((a) => (
                    <option key={a.group_assignments_id} value={a.group_assignments_id}>
                      {a.assignment_title}
                    </option>
                  ))}
                </select>
              </label>
              <label className="admin-materials-label">
                <span>Ocena / wynik</span>
                {renderGradeInput()}
              </label>
              <label className="admin-materials-label">
                <span>Uwagi</span>
                <textarea
                  className="ag-input"
                  rows={2}
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                />
              </label>
            </>
          )}

          {kind === "activity" && (
            <>
              <label className="admin-materials-label">
                <span>Typ aktywności</span>
                <select className="ag-input" value={activityType} onChange={(e) => setActivityType(e.target.value)}>
                  {ACTIVITY_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="admin-materials-label">
                <span>Waga</span>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  className="ag-input"
                  value={weight}
                  onChange={(e) => setWeight(parseFloat(e.target.value) || 1)}
                />
              </label>
              <label className="admin-materials-label">
                <span>Opis</span>
                <input
                  className="ag-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </label>
            </>
          )}

          {error && <div className="admin-materials-error">{error}</div>}

          <div className="admin-materials-modal-actions">
            <button type="button" className="admin-materials-btn" onClick={onClose} disabled={submitting}>
              Anuluj
            </button>
            <button type="submit" className="admin-materials-primary-btn" disabled={submitting}>
              {submitting ? "Zapisywanie…" : "Dodaj ocenę"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
